import * as React from 'react'
import { Sofa, Plus, ChevronRight, Image as ImageIcon, X } from 'lucide-react'
import { useVendorFurniture, useCreateVendorFurniture, useUpdateVendorFurniture } from '@/hooks/useVendorFurniture'
import { useOwnVendor } from '@/hooks/useVendors'
import { uploadService, UploadsUnavailableError } from '@/services/UploadService'
import { fileToThumbnail } from '@/helpers/imageThumbnail'
import { centsToInput, dollarsToCents } from '@/lib/price'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Select } from '@/components/ui/select'
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/states'
import { Tabs } from '@/components/ui/tabs'
import { PageContainer } from '@/components/ui/page-container'
import { PageHeader } from '@/components/ui/page-header'
import { FurnitureEditor } from '@/components/lazyDrawings'
import { DanceFloorFields } from '@/components/DanceFloorFields'
import { RiserFields } from '@/components/RiserFields'
import { FurnitureTile } from '@/components/FurnitureTile'
import { VendorFurnitureGroupsPanel } from '@/components/VendorFurnitureGroupsPanel'
import { createVendorFurnitureItemInputSchema, updateFurnitureItemInputSchema } from '@lib/input/furniture-item.input'
import { FURNITURE_CLASSES, furnitureClassLabel, type FurnitureClass } from '@lib/enums/furniture-class.enum'
import { danceFloorFormValues } from '@lib/helpers/dance-floor'
import { riserFormValues } from '@lib/helpers/riser'
import type { Primitive } from '@lib/dto/primitive.dto'
import type { FurnitureItemDto, FurnitureItemListDto } from '@lib/dto/furniture-item.dto'
import type { VendorRentalUse } from '@lib/dto/plan-impact.dto'
import { formatDate } from '@lib/helpers/format'
import type { CalendarDay } from '@lib/validators/temporal.validator'
import { vendorFurnitureService } from '@/services/VendorFurnitureService'
import { Callout } from '@/components/ui/callout'

/**
 * A whole stock count of 0 or more. Blank, partial, and negative text are
 * null: those used to be stored as 0, which wiped a count the vendor did not type.
 */
export function stockQuantity(value: string): number | null {
  const trimmed = value.trim()
  if (!/^\d+$/.test(trimmed)) return null
  const qty = Number(trimmed)
  return Number.isSafeInteger(qty) ? qty : null
}
import { FormError } from '@/components/ui/form-error'

/**
 * The upcoming days on which events have rented more of a piece than `stock`,
 * with what each day holds. Rentals on one day are counted together — the same
 * day is the rule the planner's own cap uses when it shares a vendor's stock
 * between events.
 */
function daysOverStock(
  rentals: readonly VendorRentalUse[],
  stock: number,
): Array<{ day: CalendarDay; rented: VendorRentalUse[]; total: number }> {
  const byDay = new Map<CalendarDay, VendorRentalUse[]>()
  for (const r of rentals) {
    if (r.eventDate === null) continue
    byDay.set(r.eventDate, [...(byDay.get(r.eventDate) ?? []), r])
  }
  return [...byDay.entries()]
    .map(([day, rented]) => ({ day, rented, total: rented.reduce((n, r) => n + r.quantity, 0) }))
    .filter((d) => d.total > stock)
    .sort((a, b) => a.day.localeCompare(b.day))
}
function furnitureClassValue(value: string): FurnitureClass {
  return FURNITURE_CLASSES.find((c) => c === value) ?? 'Other'
}

/** What the dialog asks for, which depends on whether the piece is drawn or measured. */
function dialogDescription(furnitureClass: FurnitureClass): string {
  if (furnitureClass === 'DanceFloor') {
    return 'Name it, set how many you stock, then set the size it starts at and the step it can grow by.'
  }
  if (furnitureClass === 'Riser') {
    return 'Name it, set how many units you stock, then set how wide and how long one unit is.'
  }
  return 'Name it, set how many you stock, then draw its shape.'
}

/** A vendor's own furniture library: items carry a stock `quantity` (the cap when
 * they appear in a linked venue's events) and reusable groups, both flat (no
 * venue scoping — a vendor's catalog spans every venue it's linked to). */
export function VendorFurniturePage() {
  const ownVendor = useOwnVendor()
  const furnitureQuery = useVendorFurniture()
  const createFurniture = useCreateVendorFurniture()
  const updateFurniture = useUpdateVendorFurniture()

  const [open, setOpen] = React.useState(false)
  const [tab, setTab] = React.useState<'items' | 'groups'>('items')
  const [editingId, setEditingId] = React.useState<string | null>(null)
  const [name, setName] = React.useState<string>('')
  const [furnitureClass, setFurnitureClass] = React.useState<FurnitureClass>('Other')
  const [quantity, setQuantity] = React.useState<string>('0')
  const [price, setPrice] = React.useState<string>('')
  const [description, setDescription] = React.useState<string>('')
  const [danceFloorDefault, setDanceFloorDefault] = React.useState('')
  const [danceFloorIncrement, setDanceFloorIncrement] = React.useState('')
  const [riserWidth, setRiserWidth] = React.useState('')
  const [riserLength, setRiserLength] = React.useState('')
  const [primitives, setPrimitives] = React.useState<Primitive[]>([])
  const [imageUrl, setImageUrl] = React.useState<string | null>(null)
  /** Signed/local preview when `imageUrl` is a private storage key after upload. */
  const [imagePreview, setImagePreview] = React.useState<string | null>(null)
  const [dragging, setDragging] = React.useState(false)
  const [submitError, setSubmitError] = React.useState<string | null>(null)
  /**
   * Days on which upcoming events already rent more than the stock being saved.
   * Shown in the form, and the next save goes ahead: the vendor owns the count,
   * but should know which events it leaves short.
   */
  const [stockWarning, setStockWarning] = React.useState<ReturnType<typeof daysOverStock> | null>(null)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  /** Bumped when a newer photo is picked or the current one is cleared, so a slow upload cannot land on the wrong item. */
  const imageUpload = React.useRef(0)
  const [imageUploading, setImageUploading] = React.useState(false)
  const displayImage = imagePreview ?? imageUrl

  const busy = createFurniture.isPending || updateFurniture.isPending

  function cancelImageUpload(): void {
    imageUpload.current += 1
    setImageUploading(false)
  }
  const danceFloor = danceFloorFormValues(furnitureClass, danceFloorDefault, danceFloorIncrement)
  const danceFloorError = danceFloor.error
  const riser = riserFormValues(furnitureClass, riserWidth, riserLength)
  const measureError = danceFloorError ?? riser.error

  // A dance floor and a riser are their measurements; everything else is drawn.
  function renderShapeFields() {
    if (furnitureClass === 'DanceFloor') {
      return (
        <DanceFloorFields
          idPrefix="vfi"
          defaultFeet={danceFloorDefault}
          incrementFeet={danceFloorIncrement}
          onDefaultFeet={setDanceFloorDefault}
          onIncrementFeet={setDanceFloorIncrement}
          error={danceFloorError}
        />
      )
    }
    if (furnitureClass === 'Riser') {
      return (
        <RiserFields
          idPrefix="vfi"
          widthFeet={riserWidth}
          lengthFeet={riserLength}
          onWidthFeet={setRiserWidth}
          onLengthFeet={setRiserLength}
          error={riser.error}
        />
      )
    }
    return (
      <Field label="Shape">
        <FurnitureEditor primitives={primitives} onChange={setPrimitives} />
      </Field>
    )
  }

  function openCreate(): void {
    cancelImageUpload()
    setEditingId(null)
    setName('')
    setFurnitureClass('Other')
    setQuantity('0')
    setPrice('')
    setDescription('')
    setDanceFloorDefault('')
    setDanceFloorIncrement('')
    setRiserWidth('')
    setRiserLength('')
    setPrimitives([])
    setImageUrl(null)
    setImagePreview(null)
    setSubmitError(null)
    setStockWarning(null)
    setOpen(true)
  }

  function openEdit(item: FurnitureItemDto): void {
    cancelImageUpload()
    setEditingId(item.id)
    setName(item.name)
    setFurnitureClass(item.class)
    setQuantity(String(item.quantity ?? 0))
    setPrice(centsToInput(item.price))
    setDescription(item.description ?? '')
    setDanceFloorDefault(item.danceFloorDefaultFeet === null ? '' : String(item.danceFloorDefaultFeet))
    setDanceFloorIncrement(item.danceFloorIncrementFeet === null ? '' : String(item.danceFloorIncrementFeet))
    setRiserWidth(item.riserUnitWidthFeet === null ? '' : String(item.riserUnitWidthFeet))
    setRiserLength(item.riserUnitLengthFeet === null ? '' : String(item.riserUnitLengthFeet))
    setPrimitives(item.primitives)
    setImageUrl(item.imageUrl)
    setImagePreview(null)
    setSubmitError(null)
    setStockWarning(null)
    setOpen(true)
  }

  async function loadImageFile(file: File): Promise<void> {
    if (!file.type.startsWith('image/')) {
      setSubmitError('That file is not an image.')
      return
    }
    const generation = imageUpload.current + 1
    imageUpload.current = generation
    setImageUploading(true)
    try {
      const dataUrl = await fileToThumbnail(file)
      if (imageUpload.current !== generation) return
      try {
        const uploaded = await uploadService.uploadImage(dataUrl)
        if (imageUpload.current !== generation) return
        setImageUrl(uploaded.key)
        setImagePreview(uploaded.url)
      } catch (uploadError) {
        if (imageUpload.current !== generation) return
        if (!(uploadError instanceof UploadsUnavailableError)) throw uploadError
        setImageUrl(dataUrl)
        setImagePreview(null)
      }
      setSubmitError(null)
    } catch (error) {
      if (imageUpload.current === generation) setSubmitError(requestErrorMessage(error))
    } finally {
      if (imageUpload.current === generation) setImageUploading(false)
    }
  }

  async function handleFile(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (file) await loadImageFile(file)
  }

  async function handleImageDrop(event: React.DragEvent<HTMLDivElement>): Promise<void> {
    event.preventDefault()
    setDragging(false)
    const file = event.dataTransfer.files?.[0]
    if (file) await loadImageFile(file)
  }

  function onOpenChange(next: boolean): void {
    setOpen(next)
    if (!next) {
      cancelImageUpload()
      setSubmitError(null)
    }
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setSubmitError(null)
    if (imageUploading) return
    const quantityValue = stockQuantity(quantity)
    if (quantityValue === null) {
      setSubmitError('Enter a whole quantity of 0 or more.')
      return
    }
    const priceValue = dollarsToCents(price)
    if (priceValue === undefined) {
      setSubmitError('Enter the price in dollars, or leave it blank.')
      return
    }
    const descriptionValue = description.trim() === '' ? null : description.trim()
    try {
      if (editingId) {
        const before = furnitureQuery.data?.items.find((i) => i.id === editingId)?.quantity ?? 0
        if (quantityValue < before && stockWarning === null) {
          // Lowering stock: which upcoming days are already rented past it?
          const usage = await vendorFurnitureService.itemUsage(editingId)
          const short = daysOverStock(usage.rentals, quantityValue)
          if (short.length > 0) {
            setStockWarning(short)
            return
          }
        }
        const parsed = updateFurnitureItemInputSchema.safeParse({
          name,
          class: furnitureClass,
          primitives,
          imageUrl,
          quantity: quantityValue,
          price: priceValue,
          description: descriptionValue,
          danceFloorDefaultFeet: danceFloor.defaultFeet,
          danceFloorIncrementFeet: danceFloor.incrementFeet,
          riserUnitWidthFeet: riser.widthFeet,
          riserUnitLengthFeet: riser.lengthFeet,
        })
        if (!parsed.success) {
          setSubmitError(parsed.error.issues[0]?.message ?? 'Invalid input')
          return
        }
        await updateFurniture.mutateAsync({ id: editingId, input: parsed.data })
      } else {
        const parsed = createVendorFurnitureItemInputSchema.safeParse({
          name,
          class: furnitureClass,
          primitives,
          quantity: quantityValue,
          price: priceValue,
          description: descriptionValue,
          danceFloorDefaultFeet: danceFloor.defaultFeet,
          danceFloorIncrementFeet: danceFloor.incrementFeet,
          riserUnitWidthFeet: riser.widthFeet,
          riserUnitLengthFeet: riser.lengthFeet,
          ...(imageUrl ? { imageUrl } : {}),
        })
        if (!parsed.success) {
          setSubmitError(parsed.error.issues[0]?.message ?? 'Invalid input')
          return
        }
        await createFurniture.mutateAsync(parsed.data)
      }
      setOpen(false)
    } catch (error) {
      setSubmitError(requestErrorMessage(error))
    }
  }

  function renderList() {
    if (furnitureQuery.isPending) return <LoadingState label="Loading furniture…" />
    if (furnitureQuery.isError) {
      return <ErrorState message={requestErrorMessage(furnitureQuery.error)} onRetry={() => void furnitureQuery.refetch()} />
    }
    const list: FurnitureItemListDto = furnitureQuery.data
    if (list.items.length === 0) {
      return (
        <EmptyState
          icon={Sofa}
          title="No furniture yet"
          description="Add your first piece; it becomes available in the events of venues you’re linked to."
          action={
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" /> New item
            </Button>
          }
        />
      )
    }
    return (
      <ul className="divide-y divide-line overflow-hidden rounded-md border border-line">
        {list.items.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              onClick={() => openEdit(item)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-surface-hover"
            >
              {item.imageUrl ? (
                <img src={item.imageUrl} alt="" className="h-11 w-11 shrink-0 rounded-md border border-line object-cover" />
              ) : (
                <FurnitureTile primitives={item.primitives} size={44} />
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{item.name}</span>
                <span className="text-xs text-ink-muted">
                  {furnitureClassLabel(item.class)} · {item.quantity ?? 0} in stock
                </span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-ink-muted" />
            </button>
          </li>
        ))}
      </ul>
    )
  }

  // Furniture is gated on offering the service; if it's off, point back to the
  // Vendor page rather than showing an (empty, 403-ing) library.
  if (ownVendor.data && !ownVendor.data.services.includes('furniture')) {
    return (
      <PageContainer width="wide" className="space-y-6">
        <PageHeader title="Furniture" description="Your supply catalog and reusable groups." />
        <EmptyState
          icon={Sofa}
          title="Furniture isn't enabled"
          description="Turn on the Furniture service on your Vendor page to manage a library."
        />
      </PageContainer>
    )
  }

  return (
    <PageContainer width="wide" className="space-y-6">
      <PageHeader title="Furniture" description="Your supply catalog and reusable groups." />

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v === 'groups' ? 'groups' : 'items')}
        items={[
          { value: 'items', label: 'Items' },
          { value: 'groups', label: 'Groups' },
        ]}
      />

      {tab === 'items' ? (
        <>
          <div className="flex justify-end">
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" /> New item
            </Button>
          </div>
          {renderList()}
        </>
      ) : (
        <VendorFurnitureGroupsPanel />
      )}

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[88vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit furniture item' : 'New furniture item'}</DialogTitle>
            <DialogDescription>{dialogDescription(furnitureClass)}</DialogDescription>
          </DialogHeader>
          <form className="space-y-5" onSubmit={handleSubmit} noValidate>
            <div className="grid gap-4 sm:grid-cols-3">
              <Field label="Name" htmlFor="vfi-name" className="sm:col-span-1">
                <Input id="vfi-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Gold Chiavari Chair" autoFocus />
              </Field>
              <Field label="Class" htmlFor="vfi-class">
                <Select
                  id="vfi-class"
                  value={furnitureClass}
                  onChange={(e) => setFurnitureClass(furnitureClassValue(e.target.value))}
                >
                  {FURNITURE_CLASSES.map((c) => (
                    <option key={c} value={c}>
                      {furnitureClassLabel(c)}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Quantity in stock" htmlFor="vfi-qty">
                <Input
                  id="vfi-qty"
                  type="number"
                  min={0}
                  value={quantity}
                  onChange={(e) => {
                    setQuantity(e.target.value)
                    setStockWarning(null)
                  }}
                  placeholder="0"
                />
              </Field>
              {stockWarning ? (
                <Callout
                  tone="attention"
                  className="sm:col-span-2"
                  title={`Upcoming events already rent more than ${String(quantity)} on these days:`}
                >
                  <ul className="mt-1 list-disc pl-5">
                    {stockWarning.map((d) => (
                      <li key={d.day}>
                        {formatDate(d.day)}: {d.total} rented —{' '}
                        {d.rented.map((r) => `${r.venueName} (${String(r.quantity)})`).join(', ')}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-1">Their rentals stay as they are. Save again to lower the stock anyway.</p>
                </Callout>
              ) : null}
              <Field label="Price / unit ($)" htmlFor="vfi-price">
                <Input
                  id="vfi-price"
                  type="number"
                  min={0}
                  step="0.01"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="e.g. 12.50"
                />
              </Field>
              <Field label="Description" htmlFor="vfi-desc" className="sm:col-span-2">
                <Input
                  id="vfi-desc"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Material, color, etc. (optional)"
                />
              </Field>
            </div>
            <Field label="Thumbnail">
              <div
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    fileInputRef.current?.click()
                  }
                }}
                onDragOver={(e) => {
                  e.preventDefault()
                  setDragging(true)
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleImageDrop}
                className={`flex cursor-pointer items-center gap-3 rounded-md border border-dashed px-3 py-3 transition-colors ${
                  dragging ? 'border-primary bg-accent-soft' : 'border-line hover:bg-surface-hover'
                }`}
              >
                {displayImage ? (
                  <img src={displayImage} alt="" className="h-14 w-14 shrink-0 rounded-md border border-line object-cover" />
                ) : (
                  <div className="grid h-14 w-14 shrink-0 place-items-center rounded-md bg-surface-sunken text-ink-muted">
                    <ImageIcon className="h-5 w-5" />
                  </div>
                )}
                <div className="min-w-0 flex-1 text-sm">
                  <p className="font-medium text-ink">{imageUrl ? 'Replace photo' : 'Add a photo'}</p>
                  <p className="text-ink-muted">Drag an image here, or click to browse — optional.</p>
                </div>
                {imageUrl ? (
                  <button
                    type="button"
                    aria-label="Remove photo"
                    onClick={(e) => {
                      e.stopPropagation()
                      cancelImageUpload()
                      setImageUrl(null)
                      setImagePreview(null)
                    }}
                    className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-ink-muted transition-colors hover:bg-surface hover:text-ink"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
              </div>
            </Field>
            {renderShapeFields()}
            {submitError ? <FormError>{submitError}</FormError> : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={measureError !== null || imageUploading}
                pending={busy || imageUploading}
              >
                <Plus className="h-4 w-4" />
                {busy ? 'Saving…' : editingId ? 'Save changes' : 'Add item'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
